import { useState, useCallback, useMemo, type ReactNode } from 'react';
import type { ChatContext as ChatCtxType } from './types';
import { getProactiveMessage } from './proactiveMessage';
import { ChatStateContext, type ChatStateContextValue } from './chatState';

type Props = {
  context: ChatCtxType;
  children: ReactNode;
};

export function ChatProvider({ context, children }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);

  const proactive = useMemo(() => getProactiveMessage(context), [context]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    setInitialMessage(null);
  }, []);
  const toggle = useCallback(() => setIsOpen(value => !value), []);

  const value = useMemo<ChatStateContextValue>(() => ({
    isOpen,
    open,
    close,
    toggle,
    context,
    initialMessage,
    setInitialMessage,
    proactive,
  }), [isOpen, open, close, toggle, context, initialMessage, proactive]);

  return <ChatStateContext.Provider value={value}>{children}</ChatStateContext.Provider>;
}
